import { useCallback, type RefObject } from 'react';
import { useGcsStore } from '../store/useGcsStore';
import { useSelectedVehicleStore } from '../store/useSelectedVehicleStore';
import type { VehicleId } from '../types/gcs/vehicle';
import type { MapRef } from 'react-map-gl/mapbox';
import { fitMapToCoordinates, getVehicleFocusCoordinates } from '../utils/map';

/**
 * Custom hook to focus the map on a specific vehicle and its mission context.
 *
 * @param mapRef - Reference to the Mapbox map instance
 * @returns A function to focus on a vehicle given gcsId and vehicleId
 */
export const useFocusVehicle = (mapRef: RefObject<MapRef | null>) => {
  const selectedVehicle = useSelectedVehicleStore((state) => state.selectedVehicle);

  const focusVehicle = useCallback(
    (gcsId: string, vehicleId: VehicleId) => {
      if (!mapRef.current) {
        return;
      }

      const gcsData = useGcsStore.getState().data.get(gcsId);
      if (!gcsData) {
        return;
      }

      const coordinates = getVehicleFocusCoordinates(gcsData, vehicleId);
      if (coordinates.length === 0) {
        return;
      }

      fitMapToCoordinates(mapRef.current, coordinates, !!selectedVehicle);
    },
    [mapRef, selectedVehicle]
  );

  return focusVehicle;
};
